import CarouselCard from '@client/components/shared/VendorProfilesCarousel/CarouselCard/CarouselCard';
import { VendorProfilesCarousel } from '@client/components/shared/VendorProfilesCarousel/VendorProfilesCarousel';
import { selectedCategoryCodesAtom } from '@pages/VendorsSearch/containers/FilterPills/atoms';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import startCase from 'lodash/startCase';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import FeaturedSectionLayout from '../../../../components/shared/FeaturedSectionLayout';
import SectionHeader from '../../../../components/shared/SectionHeader';
import TrackableContainer from '../../../../components/shared/TrackableContainer';
import ViewAllButton from '../../../../components/shared/ViewAllButton';
import { AnalyticsContext } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { getMemberId } from '../../../../redux';
import { getCategory } from '../../../../redux/category';
import { getIpAddress } from '../../../../redux/location';
import {
	TOP_CITY_VENDOR_LIMIT,
	getRecommendedVendorProfilesByCity,
	getRecommendedVendorTotalByCity,
	isRecommendationPermittedByCity,
} from '../../../../redux/statePage';
import { getStateCode } from '../../../../redux/states';
import Styles from '../../../../styles.scss';
import {
	ensureWeddingPrefix,
	generateCategoryDisplayName,
} from '../../../../utils/categoryContentHelper';
import { renderViewAllText } from '../../../../utils/text';
import {
	BANDED_DISTANCE,
	CLICK_THROUGH_TO_VENDORS,
	CLICK_THROUGH_TO_VENDOR_CATEGORY,
	RECOMMENDED_VENDORS,
	STATE_LANDING_PAGE,
	widgetViewed,
} from '../../../../utils/tracking';
import urlSafe, {
	INTERNAL_REFERRER_SORT_PARAM,
} from '../../../../utils/urlHelpers';
import ViewAllButtonStyles from '../../AdditionalCitiesRecommendations/CityRecommendations/styles.scss';
import TopCityStyles from '../../styles.scss';

class CityVendorRecommendations extends Component {
	sourceInfo = {
		sourcePage: STATE_LANDING_PAGE,
		sourceContent: RECOMMENDED_VENDORS,
		sourceFormula: BANDED_DISTANCE,
	};

	trackCardClick = () => {
		this.context.track({
			event: CLICK_THROUGH_TO_VENDORS,
			properties: {
				...this.sourceInfo,
				vendorCategoryCode: this.props.category.code,
			},
		});
	};

	trackViewAllClick = () =>
		this.context.track({
			event: CLICK_THROUGH_TO_VENDOR_CATEGORY,
			properties: {
				sourcePage: STATE_LANDING_PAGE,
				sourceContent: RECOMMENDED_VENDORS,
				vendorCategoryCode: this.props.category.code,
			},
		});

	render() {
		const {
			category,
			city,
			headerName,
			ipAddress,
			memberId,
			stateCode,
			vendors,
			total,
			isPermitted,
		} = this.props;
		const citySearchSlug = `/marketplace/${urlSafe(
			`${category.plural.slug}-${city}-${stateCode}`,
		)}?${INTERNAL_REFERRER_SORT_PARAM}`;

		if (vendors.length < TOP_CITY_VENDOR_LIMIT || !isPermitted) return null;

		const slicedProfiles = vendors.slice(0, TOP_CITY_VENDOR_LIMIT);

		return (
			<FeaturedSectionLayout
				header={
					<SectionHeader>
						<a className={TopCityStyles.clickableHeader} href={citySearchSlug}>
							{`${headerName} `}
							<span className={Styles.whiteSpaceNowrap}>
								near {startCase(city)}, {stateCode}
							</span>
						</a>
					</SectionHeader>
				}
				cta={
					<ViewAllButton
						className={classNames(
							ViewAllButtonStyles.viewAllButton,
							ViewAllButtonStyles.viewAllButtonWide,
						)}
						onClick={this.trackViewAllClick}
						slug={citySearchSlug}
						text={renderViewAllText(total, true)}
					/>
				}
			>
				<TrackableContainer
					trackingData={widgetViewed(STATE_LANDING_PAGE, category.code)}
				>
					<VendorProfilesCarousel label="Vendor profiles carousel" grid>
						{slicedProfiles.map((vendor, i) => (
							<CarouselCard
								key={`carousel-card-${vendor.id}`}
								index={i}
								city={city}
								impressionType={`Landings: State Large ${city}`}
								trackCardClick={this.trackCardClick}
								categoryCode={category.code}
								sourceInfo={this.sourceInfo}
								ipAddress={ipAddress}
								memberId={memberId}
								vendorRaw={vendor}
							/>
						))}
					</VendorProfilesCarousel>
				</TrackableContainer>
			</FeaturedSectionLayout>
		);
	}
}

CityVendorRecommendations.defaultProps = {
	ipAddress: null,
	isPermitted: true,
	memberId: null,
};

CityVendorRecommendations.propTypes = {
	category: PropTypes.shape({
		name: PropTypes.string.isRequired,
		code: PropTypes.string.isRequired,
		plural: PropTypes.shape({
			slug: PropTypes.string.isRequired,
		}).isRequired,
	}).isRequired,
	city: PropTypes.string.isRequired,
	headerName: PropTypes.string.isRequired,
	ipAddress: PropTypes.string,
	isPermitted: PropTypes.bool,
	memberId: PropTypes.string,
	stateCode: PropTypes.string.isRequired,
	track: PropTypes.func.isRequired,
	total: PropTypes.number.isRequired,
	vendors: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
};

function mapStateToProps(state, { city }) {
	return {
		category: getCategory(state),
		headerName: ensureWeddingPrefix(
			generateCategoryDisplayName(state.category),
		),
		ipAddress: getIpAddress(state),
		memberId: getMemberId(state),
		stateCode: getStateCode(state),
		total: getRecommendedVendorTotalByCity(state, city),
		vendors: getRecommendedVendorProfilesByCity(state, city),
		isPermitted: isRecommendationPermittedByCity(city, state),
	};
}

const ConnectedCityVendorRecommendations = compose(connect(mapStateToProps))(
	CityVendorRecommendations,
);

const CityVendorRecommendationsWithSelectedCategories = (props) => {
	const selectedCategoryCodes = useAtomValue(selectedCategoryCodesAtom);
	return (
		<ConnectedCityVendorRecommendations
			{...props}
			selectedCategoryCodes={selectedCategoryCodes}
		/>
	);
};

CityVendorRecommendations.contextType = AnalyticsContext;

export default CityVendorRecommendationsWithSelectedCategories;
